import { useState } from "react";
import { MiBox, MiDatePicker, MiDetailFields } from "@miplatform/components";
import {
  DATE_TIME_FORMATS,
  DOCUMENT_TYPES,
  INPUT_TYPES,
} from "@miplatform/constants";
import { IDocumentListItemResponse } from "@miplatform/types";
import { externalReferenceId, toFormattedDate } from "@miplatform/utils";
import { useDocumentsViewModel } from "@miplatform/view-models";
import { useCreateBidContext } from "../../../CreateBidContext";
import { DocCategory } from "../../../CreateBids.types";
import { DOC_CATEGORY } from "../../../constants";
import { InputFile } from "./InputFile/InputFile";
import { UploadDocumentModal } from "./UploadDocumentModal/UploadDocumentModal";

export function BidDetailsForm({ open }: BidDetailsFormProps) {
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [docCategory, setDocCategory] = useState<DocCategory | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentLabel, setDocumentLabel] = useState("");

  const { form, communities, accounts } = useCreateBidContext();
  const { control, setValue, watch } = form;
  const { id, dueDate, documents = [] } = watch();
  const { loading, uploadDocument, removeDocument } = useDocumentsViewModel();

  if (!open) {
    return null;
  }

  const planDoc = documents.find((d) => d.category === DOC_CATEGORY.PLAN);
  const specDoc = documents.find((d) => d.category === DOC_CATEGORY.SPEC);
  const otherDocs = documents.filter(
    (d) => d.category !== DOC_CATEGORY.PLAN && d.category !== DOC_CATEGORY.SPEC
  );

  const handleOpenUploadModal = (category: DocCategory) => {
    setDocCategory(category);
    setDocumentLabel(category === DOC_CATEGORY.OTHER ? "" : category);
    setSelectedFile(null);
    setOpenUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setDocCategory(null);
    setSelectedFile(null);
    setDocumentLabel("");
    setOpenUploadModal(false);
  };

  const handleUploadFile = async () => {
    if (!selectedFile || !docCategory) return;
    const category =
      docCategory === DOC_CATEGORY.OTHER ? documentLabel : docCategory;
    const uploadedDoc = await uploadDocument({
      file: selectedFile,
      documentType: DOCUMENT_TYPES.UNKNOWN,
      category,
      externalReferenceId: externalReferenceId.getBidRefId({ bidId: id }),
    });
    if (!uploadedDoc) return;
    const addedDoc = [...documents, uploadedDoc] as IDocumentListItemResponse[];
    setValue("documents", addedDoc, { shouldValidate: true });
    handleCloseUploadModal();
  };

  const handleRemoveFile = async (doc: IDocumentListItemResponse) => {
    const success = await removeDocument(doc);
    if (!success) return;
    const removedDoc = documents.filter((d) => d.id !== doc.id);
    setValue("documents", removedDoc, { shouldValidate: true });
  };

  const bidDetailFields = [
    {
      label: "Bid Name",
      name: "name",
      formControl: control,
      width: "50%",
      required: true,
    },
    {
      label: "Customer",
      name: "builderId",
      fieldType: INPUT_TYPES.SELECT,
      formControl: control,
      width: "50%",
      required: true,
      options: accounts.map((a) => ({ label: a.name, value: a.id })),
    },
    {
      label: "Community",
      name: "communityId",
      fieldType: INPUT_TYPES.SELECT,
      formControl: control,
      width: "50%",
      required: true,
      options: communities.map((c) => ({ label: c.name, value: c.id })),
    },
    {
      label: "Due Date",
      name: "dueDate",
      fieldType: INPUT_TYPES.CUSTOM,
      formControl: control,
      width: "50%",
      required: true,
      customComponent: (
        <MiDatePicker
          isFullWidth
          disablePast
          readOnly
          value={dueDate}
          onChange={(date: Date) => {
            const formattedDate = toFormattedDate(
              date,
              DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
            );
            setValue("dueDate", formattedDate, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
        />
      ),
    },
    {
      label: "Additional House Features (e.g. Front Entry - Electric)",
      name: "additionalFeatures",
      formControl: control,
      width: "50%",
      required: true,
    },
    {
      label: "Pipe Brand",
      name: "pipeBrand",
      formControl: control,
      width: "50%",
    },
    {
      fieldType: INPUT_TYPES.CUSTOM,
      width: "50%",
      customComponent: (
        <InputFile
          doc={planDoc}
          handleRemoveFile={handleRemoveFile}
          handleOpenUploadModal={() => handleOpenUploadModal(DOC_CATEGORY.PLAN)}
          label="Upload Plan File"
          required
        />
      ),
    },
    {
      fieldType: INPUT_TYPES.CUSTOM,
      width: "50%",
      customComponent: (
        <InputFile
          doc={specDoc}
          handleRemoveFile={handleRemoveFile}
          handleOpenUploadModal={() => handleOpenUploadModal(DOC_CATEGORY.SPEC)}
          label="Upload Specification File"
          required
        />
      ),
    },
    ...otherDocs.map((otherDoc) => ({
      fieldType: INPUT_TYPES.CUSTOM,
      width: "50%",
      customComponent: (
        <InputFile doc={otherDoc} handleRemoveFile={handleRemoveFile} />
      ),
    })),
    {
      fieldType: INPUT_TYPES.CUSTOM,
      width: "50%",
      customComponent:
        otherDocs.length < 2 ? (
          <InputFile
            handleOpenUploadModal={() =>
              handleOpenUploadModal(DOC_CATEGORY.OTHER)
            }
            label="Upload Other Files"
          />
        ) : (
          <div />
        ),
    },
    {
      fieldType: INPUT_TYPES.CUSTOM,
      width: "50%",
      customComponent: <div />,
    } as any,
  ];

  return (
    <MiBox headerTitle="Bid Details">
      <MiDetailFields detailFields={bidDetailFields} />
      <UploadDocumentModal
        open={openUploadModal}
        docCategory={docCategory}
        documentLabel={documentLabel}
        selectedFile={selectedFile}
        loading={loading}
        handleCloseUploadModal={handleCloseUploadModal}
        handleUploadFile={handleUploadFile}
        setDocumentLabel={setDocumentLabel}
        setSelectedFile={setSelectedFile}
      />
    </MiBox>
  );
}

interface BidDetailsFormProps {
  open: boolean;
}
